import { BugReportService } from '../firebase/unifiedDataService';
import { Logger } from '../utils/logger';

const log = Logger.create('BugReportService');

/**
 * Bug Report Service
 * 
 * Handles submission and management of bug reports.
 * Uses unifiedDataService.BugReportService for data access.
 */

/**
 * Submit a bug report to Firestore
 * 
 * @param {Object} bugReport - Bug report data
 * @returns {Promise<string>} - Document ID of created bug report
 */
export async function submitBugReport(bugReport) {
  try {
    const docId = await BugReportService.submit(bugReport);
    log.success('Bug report submitted successfully:', docId);
    return docId;
  } catch (error) {
    log.error('Error submitting bug report:', error);
    throw new Error('Failed to submit bug report');
  }
}

/**
 * Get browser information
 * 
 * @returns {Object} Browser info
 */
export function getBrowserInfo() {
  const ua = navigator.userAgent;
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';

  // Detect browser
  if (ua.includes('Firefox/')) {
    browserName = 'Firefox';
    browserVersion = ua.match(/Firefox\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Edg/')) {
    browserName = 'Edge';
    browserVersion = ua.match(/Edg\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
    browserName = 'Chrome';
    browserVersion = ua.match(/Chrome\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browserName = 'Safari';
    browserVersion = ua.match(/Version\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Opera/') || ua.includes('OPR/')) {
    browserName = 'Opera';
    browserVersion = ua.match(/(Opera|OPR)\/([0-9.]+)/)?.[2] || 'Unknown';
  }

  return {
    name: browserName,
    version: browserVersion,
    userAgent: ua
  };
}

/**
 * Get operating system information
 * 
 * @returns {Object} OS info
 */
export function getOSInfo() {
  const ua = navigator.userAgent;
  const platform = navigator.platform;
  let osName = 'Unknown';
  let osVersion = 'Unknown';

  if (ua.includes('Win')) {
    osName = 'Windows';
    if (ua.includes('Windows NT 10.0')) osVersion = '10/11';
    else if (ua.includes('Windows NT 6.3')) osVersion = '8.1';
    else if (ua.includes('Windows NT 6.2')) osVersion = '8';
    else if (ua.includes('Windows NT 6.1')) osVersion = '7';
  } else if (ua.includes('Mac')) {
    osName = 'macOS';
    const match = ua.match(/Mac OS X ([0-9_]+)/);
    if (match) {
      osVersion = match[1].replace(/_/g, '.');
    }
  } else if (ua.includes('Linux')) {
    osName = 'Linux';
  } else if (ua.includes('Android')) {
    osName = 'Android';
    const match = ua.match(/Android ([0-9.]+)/);
    if (match) osVersion = match[1];
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    osName = 'iOS';
    const match = ua.match(/OS ([0-9_]+)/);
    if (match) osVersion = match[1].replace(/_/g, '.');
  }

  return {
    name: osName,
    version: osVersion,
    platform
  };
}

/**
 * Get screen and viewport information
 * 
 * @returns {Object} Screen info
 */
export function getScreenInfo() {
  return {
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio || 1
    }
  };
}

/**
 * Collect all system information
 * 
 * @param {boolean} isDark - Current theme mode
 * @returns {Object} Complete system info
 */
export function collectSystemInfo(isDark) {
  const browser = getBrowserInfo();
  const os = getOSInfo();
  const screen = getScreenInfo();

  return {
    browser: browser.name,
    browserVersion: browser.version,
    userAgent: browser.userAgent,
    
    os: os.name,
    osVersion: os.version,
    platform: os.platform,
    
    viewport: screen.viewport,
    screenResolution: {
      width: screen.screen.width,
      height: screen.screen.height
    },
    pixelRatio: screen.screen.pixelRatio,
    
    url: window.location.href,
    route: window.location.pathname,
    theme: isDark ? 'dark' : 'light',
    
    timestamp: new Date().toISOString()
  };
}

export default {
  submitBugReport,
  getBrowserInfo,
  getOSInfo,
  getScreenInfo,
  collectSystemInfo
};
